/**
 * datetime — the single source of truth for user-facing date & time formatting.
 *
 * Every date/time a USER sees should be rendered through one of the `friendly*`
 * helpers here. They are:
 *   • Locale-aware   — ordering (month-day vs day-month), month names, and the
 *                      AM/PM vs 24h clock all follow the configured locale via
 *                      Intl.DateTimeFormat, so adding another country later is a
 *                      config change, not a code change.
 *   • Timezone-aware — every helper accepts an explicit IANA `tz`; an event is
 *                      always rendered in its OWN venue timezone. When no tz is
 *                      passed we fall back to the app's configured display tz.
 *
 * Storage stays as-is (ISO on the wire; the wizard's legacy
 * "Wed, 6 May 2026 · 18:00" string round-trips through jrni-ui's DatePicker).
 * These helpers PARSE both and only own the DISPLAY string. Never hand-build a
 * date string with template literals in a screen — call a helper here so the
 * format is consistent and internationalizable.
 */
import { useSyncExternalStore } from "react";
import {
  getI18nConfig,
  getDateTimeDefaults,
  configureDateTime as kitConfigureDateTime,
  subscribeCombinedDateTime,
  combinedDateTimeVersion,
  formatDateRange,
} from "@v0y4ge/i18n-runtime";
import { eventCivilDayRange } from "@v0y4ge/api-contracts";

// ── App-wide config — THIN DELEGATION to @v0y4ge/i18n-runtime (GM11/GM12
//    pushback, voyage#126 class). The runtime owns BOTH stores: locale is the
//    ACTIVE i18n locale (configureI18n / setAppLocale); timezone + clock are
//    the runtime's datetime defaults. This module keeps NO copy of either —
//    every helper reads live, so a locale switch or tenant-tz change is
//    observed everywhere at once (dashboard and calendars agree by
//    construction, and switching back to English resets date formats too). ──
export interface DateTimeConfig {
  /** BCP-47 locale tag — ALWAYS the runtime's active i18n locale. */
  locale: string;
  /** IANA timezone used when a caller doesn't pass an explicit one (e.g.
   *  workspace default). undefined ⇒ the runtime's local zone. */
  timeZone: string | undefined;
  /** Force 12h/24h regardless of locale default. undefined ⇒ locale decides. */
  hour12?: boolean;
  /** Workspace calendar convention from the public theme read; absent means Monday. */
  weekStartsOn?: "monday" | "sunday" | "saturday";
}

const _locale = (): string => { try { return getI18nConfig().locale || "en-US"; } catch { return "en-US"; } };
const _tz = (): string | undefined => { try { return getDateTimeDefaults().timeZone; } catch { return undefined; } };
const _hour12 = (): boolean | undefined => { try { return getDateTimeDefaults().hour12; } catch { return undefined; } };

// Reactive layer: delegated to the runtime's combined locale + defaults
// subscription, so components re-render on EITHER change.
export function subscribeDateTime(fn: () => void): () => void {
  const off = subscribeCombinedDateTime(fn);
  _patternListeners.add(fn);
  return () => { off(); _patternListeners.delete(fn); };
}
/** Monotonic counter (locale version + defaults version + date pattern) — hook snapshot. */
export function dateTimeVersion(): number {
  return combinedDateTimeVersion() + _patternVersion;
}

/* ── The workspace's own date pattern (item 8, CC 0079/0084) ─────────────────
 * Settings > General's dateFormat ("DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD",
 * "DD.MM.YYYY", "D MMMM YYYY" …). When set, friendlyDate / friendlyDateShort print
 * the date part in it, so every screen and public page agrees with Studio. Only
 * a value made of the Settings tokens (YYYY, MMMM, MM, DD, D) and separators is
 * taken; anything else (a mock placeholder, a typo) leaves the locale default.
 * The clock (12h/24h) is the runtime's `hour12`, set through configureDateTime. */
let _datePattern: string | null = null;
let _weekStartsOn: DateTimeConfig["weekStartsOn"];
let _patternVersion = 0;
const _patternListeners = new Set<() => void>();
// MMMM = MM twice, DD = D twice: the same strings, without the exponential backtracking (CC 0103).
const DATE_PATTERN_RE = /^(?:YYYY|MM|D|[ ./,\-])+$/;
export function isDatePattern(p: unknown): p is string {
  return typeof p === "string" && p.length <= 40 && DATE_PATTERN_RE.test(p)
    && p.includes("YYYY") && /MMMM|MM/.test(p) && /D/.test(p.replace(/YYYY|MMMM|MM/g, ""));
}
export function setWorkspaceDatePattern(p: unknown): void {
  const next = isDatePattern(p) ? p : null;
  if (next === _datePattern) return;
  _datePattern = next;
  _patternVersion++;
  _patternListeners.forEach((fn) => fn());
}
export function getWorkspaceDatePattern(): string | null { return _datePattern; }
export function setWorkspaceWeekStart(value: unknown): void {
  const next = value === "monday" || value === "sunday" || value === "saturday" ? value : undefined;
  if (next === _weekStartsOn) return;
  _weekStartsOn = next;
  _patternVersion++;
  _patternListeners.forEach((fn) => fn());
}
function _patternDate(input: DateInput, tz?: string): string | null {
  if (!_datePattern) return null;
  const p = toParts(input, tz);
  if (!p) return null;
  const monthLong = (() => { try { return new Intl.DateTimeFormat(_locale(), { month: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2020, p.mo, 1))); } catch { return String(p.mo + 1); } })();
  return _datePattern.replace(/YYYY|MMMM|MM|DD|D/g, (tok) =>
    tok === "YYYY" ? String(p.y) : tok === "MMMM" ? monthLong : tok === "MM" ? String(p.mo + 1).padStart(2, "0")
      : tok === "DD" ? String(p.d).padStart(2, "0") : String(p.d));
}

/** A wall-clock time of day (hour 0-23, minute) in the configured clock: 12h or
 *  24h from the workspace setting, else the locale. For hand-built journey times
 *  ("Ends at …") that carry no instant. */
export function friendlyClock(hour: number, minute: number): string {
  try {
    const h12 = _hour12();
    return new Intl.DateTimeFormat(_locale(), { hour: "numeric", minute: "2-digit", timeZone: "UTC", ...(h12 === undefined ? {} : { hour12: h12 }) })
      .format(new Date(Date.UTC(2020, 0, 1, hour, minute)));
  } catch {
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  }
}

/** Update the display timezone / clock override — forwards to the runtime's
 *  datetime-defaults store (the single source of truth). `locale` patches are
 *  IGNORED: the locale always follows the active i18n locale (configureI18n /
 *  setAppLocale) — maintaining a second locale copy here is the exact
 *  GM11/GM12 regression. Only keys present in the patch forward, so a
 *  locale-only call can never clear the tenant timezone. Invalid IANA zones
 *  are rejected instead of poisoning every formatter. */
export function configureDateTime(patch: Partial<DateTimeConfig>): void {
  const fwd: { timeZone?: string; hour12?: boolean } = {};
  if ("timeZone" in patch) {
    let tz = patch.timeZone;
    if (tz) {
      try { new Intl.DateTimeFormat("en-US", { timeZone: tz }); }
      catch { console.warn(`configureDateTime: invalid IANA timezone "${tz}" — keeping previous`); tz = _tz(); }
    }
    fwd.timeZone = tz;
  }
  if ("hour12" in patch) fwd.hour12 = patch.hour12;
  if ("timeZone" in fwd || "hour12" in fwd) kitConfigureDateTime(fwd);
}
export function getDateTimeConfig(): DateTimeConfig {
  return { locale: _locale(), timeZone: _tz(), hour12: _hour12(), ...(_weekStartsOn ? { weekStartsOn: _weekStartsOn } : {}) };
}

/** Subscribe a component to date/time config changes. Returns the current
 *  version; any locale/timezone change re-renders the caller so inline
 *  friendly* calls re-format live. Put one at the app root to refresh the whole
 *  tree, or call it in any leaf that must react on its own. */
export function useDateTimeVersion(): number {
  return useSyncExternalStore(subscribeDateTime, dateTimeVersion, dateTimeVersion);
}

export type DateInput = Date | number | string | null | undefined;

const _MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Parse anything we might be handed into a Date:
 *  - Date / epoch number → as-is
 *  - ISO 8601 string → native parse
 *  - legacy wizard string "Wed, 6 May 2026 · 18:00" or "· 6:00 PM" → date + time
 *  Returns null when unparseable so callers can render a graceful fallback. */
export function toDate(input: DateInput): Date | null {
  if (input == null || input === "") return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input === "number") { const d = new Date(input); return isNaN(d.getTime()) ? null : d; }
  const s = String(input).trim();
  // Native parse first (handles ISO + most engine-recognized forms).
  const native = new Date(s);
  if (!isNaN(native.getTime()) && /\d{4}/.test(s) && !s.includes("·")) return native;
  // Legacy "date · time" wizard string.
  const [datePart, timePart] = s.split("·").map((x) => x.trim());
  let d = new Date(datePart);
  if (isNaN(d.getTime())) {
    // Strip a leading weekday ("Wed, ") the engine may choke on.
    d = new Date(datePart.replace(/^[A-Za-z]{3,9},?\s*/, ""));
  }
  if (isNaN(d.getTime())) return null;
  if (timePart) applyTimeString(d, timePart);
  return d;
}

/** Mutate `d`'s hours/minutes from a "18:00" or "6:00 PM" string. */
function applyTimeString(d: Date, t: string): void {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i.exec(t.trim());
  if (!m) return;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const mer = m[3]?.toUpperCase();
  if (mer === "PM" && h < 12) h += 12;
  if (mer === "AM" && h === 12) h = 0;
  d.setHours(h, min, 0, 0);
}

function _fmt(input: DateInput, opts: Intl.DateTimeFormatOptions, tz?: string): string {
  const d = toDate(input);
  if (!d) return typeof input === "string" ? input : "";
  const make = (timeZone: string | undefined) => new Intl.DateTimeFormat(_locale(), {
    timeZone,
    hour12: opts.hour !== undefined ? (_hour12() ?? undefined) : undefined,
    ...opts,
  }).format(d);
  try {
    return make(tz ?? _tz());
  } catch {
    // Bad tz (or locale) — retry in the runtime's local zone before giving up.
    try { return make(undefined); } catch { return d.toDateString(); }
  }
}

function _calendarLocale(): string {
  // The mounted calendar's established English layout is day-first. Keep that
  // exact shape while allowing every non-English locale to use its native
  // ordering and punctuation.
  const loc = _locale();
  return loc.toLowerCase().startsWith("en") ? "en-GB" : loc;
}

function _calendarFmt(input: DateInput, opts: Intl.DateTimeFormatOptions): string {
  const d = toDate(input);
  if (!d) return typeof input === "string" ? input : "";
  try {
    return new Intl.DateTimeFormat(_calendarLocale(), { timeZone: _tz(), ...opts }).format(d);
  } catch {
    return d.toDateString();
  }
}

/** Calendar day heading. English stays byte-equivalent: "Monday, 20 Jul 2026". */
export function friendlyCalendarDay(input: DateInput): string {
  return _calendarFmt(input, { weekday: "long", day: "numeric", month: "short", year: "numeric" });
}

/** Standalone month heading, e.g. "July 2026", with locale-native ordering. */
export function friendlyCalendarMonth(input: DateInput): string {
  return _calendarFmt(input, { month: "long", year: "numeric" });
}

/** Locale-aware weekday name for calendar column headers. */
export function friendlyCalendarWeekday(input: DateInput, width: "short" | "long" = "short"): string {
  return _calendarFmt(input, { weekday: width });
}

/** The weekday of a CIVIL date key ("YYYY-MM-DD"), e.g. a server's "opens next" date that is already in the
 *  place's own zone. Read from the date alone (a UTC instant formatted in UTC), so neither the browser zone
 *  nor the configured zone can move it to a neighbouring day. "" for a malformed key. */
export function friendlyCivilWeekday(dateKey: string, width: "short" | "long" = "short"): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey || "");
  if (!m) return "";
  const day = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  try { return new Intl.DateTimeFormat(_calendarLocale(), { weekday: width, timeZone: "UTC" }).format(day); }
  catch { return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][day.getUTCDay()]; }
}

/** Five-day calendar range. English preserves "20–24 Jul 2026" exactly. */
export function friendlyCalendarWeekRange(start: DateInput, end: DateInput): string {
  const a = toDate(start);
  const b = toDate(end);
  if (!a || !b) return "";
  if (_locale().toLowerCase().startsWith("en")) {
    const aDay = a.getDate();
    const bDay = b.getDate();
    const aMonth = _calendarFmt(a, { month: "short" });
    const bMonth = _calendarFmt(b, { month: "short" });
    const year = b.getFullYear();
    return a.getMonth() === b.getMonth()
      ? `${aDay}–${bDay} ${aMonth} ${year}`
      : `${aDay} ${aMonth} – ${bDay} ${bMonth} ${year}`;
  }
  try {
    const formatter = new Intl.DateTimeFormat(_calendarLocale(), { timeZone: _tz(), day: "numeric", month: "short", year: "numeric" });
    return formatter.formatRange(a, b);
  } catch {
    return `${_calendarFmt(a, { day: "numeric", month: "short" })} – ${_calendarFmt(b, { day: "numeric", month: "short", year: "numeric" })}`;
  }
}

/** Conceptual wall-clock hour used by calendar grids, independent of date. */
export function friendlyCalendarHour(hour: number): string {
  try {
    const value = new Intl.DateTimeFormat(_locale(), {
      hour: "numeric",
      minute: "2-digit",
      hour12: _hour12(),
      timeZone: "UTC",
    }).format(new Date(Date.UTC(2020, 0, 1, hour)));
    return _locale().toLowerCase().startsWith("en") ? value.replace(/AM|PM/g, (part) => part.toLowerCase()) : value;
  } catch {
    return `${String(hour).padStart(2, "0")}:00`;
  }
}

/** Decimal wall-clock value used by positioned calendar events (9.5 → "09:30"). */
export function friendlyCalendarTime(value: number): string {
  const hour = Math.floor(value);
  const minute = Math.round((value % 1) * 60);
  try {
    return new Intl.DateTimeFormat(_locale(), {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      timeZone: "UTC",
    }).format(new Date(Date.UTC(2020, 0, 1, hour, minute)));
  } catch {
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  }
}

// ── Friendly, accessible display helpers ────────────────────────────────────

/** "Wed, May 6, 2026" (locale-ordered, weekday for scannability). */
export function friendlyDate(input: DateInput, tz?: string): string {
  const pat = _patternDate(input, tz);
  if (pat) return `${_fmt(input, { weekday: "short" }, tz)}, ${pat}`;
  return _fmt(input, { weekday: "short", month: "short", day: "numeric", year: "numeric" }, tz);
}

/** "May 6, 2026" — no weekday, for dense tables/chips. */
export function friendlyDateShort(input: DateInput, tz?: string): string {
  return _patternDate(input, tz) ?? _fmt(input, { month: "short", day: "numeric", year: "numeric" }, tz);
}

/** "Mon, Sep 28" — weekday, day and month without the year, for strips and lists
 *  where the year is plain from context. Locale-ordered (a year-bearing pattern
 *  cannot drop its year, so this stays the locale's short form). */
export function friendlyDateNoYear(input: DateInput, tz?: string): string {
  return _fmt(input, { weekday: "short", month: "short", day: "numeric" }, tz);
}

/** "6:00 PM" (or "18:00" under a 24h locale/override). */
export function friendlyTime(input: DateInput, tz?: string): string {
  return _fmt(input, { hour: "numeric", minute: "2-digit" }, tz);
}

/** "Wed, May 6, 2026 · 6:00 PM" — single-field date+time. */
export function friendlyDateTime(input: DateInput, tz?: string): string {
  const date = friendlyDate(input, tz);
  const time = friendlyTime(input, tz);
  return time ? `${date} · ${time}` : date;
}

export interface EventDayHours { date: string; start: string; end: string }

/* ── An event's own wall clock (CC 0390) ─────────────────────────────────────
 * The designer stores start/end as the event zone's wall clock plus that zone's
 * offset ("2026-11-22T09:00:00-06:00"). Reading them through new Date(x).getX()
 * lays them out in the BROWSER's zone, so a Chicago event edited from India
 * showed 20:30 and an extra day. These read the stored string's own digits, as
 * DatePicker.parseDateString does. A "Z" instant (or one with no digits to read)
 * is converted into the event's zone when one is given. */
export interface WallClock { y: number; mo: number; d: number; h: number; mi: number }
const WALL_CLOCK_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/;
export function eventWallClock(iso: string | null | undefined, tz?: string | null): WallClock | null {
  const s = String(iso ?? "").trim();
  const m = WALL_CLOCK_RE.exec(s);
  const isUtc = /(?:Z|[+-]00:?00)$/i.test(s) && /T\d{2}:\d{2}/.test(s);
  if (m && !(isUtc && tz)) return { y: +m[1], mo: +m[2], d: +m[3], h: m[4] ? +m[4] : 0, mi: m[5] ? +m[5] : 0 };
  const at = new Date(s);
  if (Number.isNaN(at.getTime())) return null;
  if (tz) {
    try {
      const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(at).map((x) => [x.type, x.value]));
      return { y: +p.year, mo: +p.month, d: +p.day, h: +p.hour % 24, mi: +p.minute };
    } catch { /* an unknown zone: the string's digits below */ }
  }
  return m ? { y: +m[1], mo: +m[2], d: +m[3], h: m[4] ? +m[4] : 0, mi: m[5] ? +m[5] : 0 } : null;
}
const _pad2 = (n: number) => String(n).padStart(2, "0");
const _civilKey = (y: number, mo: number, d: number) => {
  const t = new Date(Date.UTC(y, mo - 1, d));
  return `${t.getUTCFullYear()}-${_pad2(t.getUTCMonth() + 1)}-${_pad2(t.getUTCDate())}`;
};
/** Every civil day ("YYYY-MM-DD", inclusive) from start to end, on the event's own wall clock. Capped at `max`. */
export function eventCivilDays(start: string | null | undefined, end: string | null | undefined, tz?: string | null, max = 366): string[] {
  const a = eventWallClock(start, tz);
  if (!a) return [];
  const b = (end ? eventWallClock(end, tz) : null) ?? a;
  const first = Date.UTC(a.y, a.mo - 1, a.d), last = Date.UTC(b.y, b.mo - 1, b.d);
  const out: string[] = [];
  for (let t = first; t <= last && out.length < max; t += 86_400_000) { const d = new Date(t); out.push(_civilKey(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate())); }
  return out.length ? out : [_civilKey(a.y, a.mo, a.d)];
}
/** A civil day as the host's local midnight — the shape the app's calendar grids and
 *  toLocaleDateString use for a date with no zone (see civilDateToLocal below). */
export function civilKeyToLocalDate(key: string): Date {
  return new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, Number(key.slice(8, 10)));
}
/** The offset ("-06:00") the zone `tz` has AT that wall clock, DST-aware for the event's own
 *  date (two passes: the offset can change between the guess and the instant). null for a
 *  zone the browser does not know. */
export function zoneOffsetAtWallClock(w: WallClock, tz: string): string | null {
  const offsetMin = (ms: number): number | null => {
    try {
      const name = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "longOffset" }).formatToParts(new Date(ms)).find((p) => p.type === "timeZoneName")?.value ?? "";
      if (name === "GMT") return 0;
      const m = /GMT([+-])(\d{2}):(\d{2})/.exec(name);
      return m ? (m[1] === "-" ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) : null;
    } catch { return null; }
  };
  const guess = Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi);
  const o1 = offsetMin(guess);
  if (o1 === null) return null;
  const o = offsetMin(guess - o1 * 60_000) ?? o1;
  const a = Math.abs(o);
  return `${o >= 0 ? "+" : "-"}${_pad2(Math.floor(a / 60))}:${_pad2(a % 60)}`;
}
/** "YYYY-MM-DD" + "HH:MM" in `tz` as an ISO instant with that zone's offset; the naive
 *  local string when there is no usable zone (read and shown in the same zone then). */
export function zonedWallClockIso(dateKey: string, hhmm: string, tz?: string | null): string {
  const [h, mi] = (/^\d{1,2}:\d{2}$/.test(hhmm) ? hhmm : "00:00").split(":").map(Number);
  const base = `${dateKey}T${_pad2(h)}:${_pad2(mi)}:00`;
  if (!tz) return base;
  const off = zoneOffsetAtWallClock({ y: +dateKey.slice(0, 4), mo: +dateKey.slice(5, 7), d: +dateKey.slice(8, 10), h, mi }, tz);
  return off ? base + off : base;
}

/** Calendar days (inclusive) an event spans, each with its own hours — a
 * stored per-day override when one exists, else the overall event's start/end
 * wall-clock time-of-day. A single-day event returns exactly one entry. Days and
 * hours are read from the stored strings' OWN wall clock (the event's zone), never
 * the browser's (CC 0390). */
export function resolveEventDayHours(cfg: { start?: string; end?: string; timezone?: string | null; dayHours?: EventDayHours[] }): EventDayHours[] {
  if (!cfg.start) return [];
  const s = eventWallClock(cfg.start, cfg.timezone);
  if (!s) return [];
  const e = cfg.end ? eventWallClock(cfg.end, cfg.timezone) : s;
  const toHM = (w: WallClock) => `${_pad2(w.h)}:${_pad2(w.mi)}`;
  const defaultStart = toHM(s);
  const defaultEnd = e ? toHM(e) : defaultStart;
  const overrides = new Map((cfg.dayHours || []).map((d) => [d.date, d]));
  return eventCivilDays(cfg.start, e ? cfg.end : cfg.start, cfg.timezone).map((key) => {
    const ov = overrides.get(key);
    return ov ? { date: key, start: ov.start, end: ov.end } : { date: key, start: defaultStart, end: defaultEnd };
  });
}

/** "Sun, Nov 22 – Wed, Nov 25, 2026" for an event whose first and last civil days differ
 *  in its OWN zone; "" for a one-day event (callers keep their single-day label). An end
 *  at midnight belongs to the day before (eventCivilDayRange). */
export function eventDayRangeLabel(startIso: string | null | undefined, endIso: string | null | undefined, tz: string | null | undefined): string {
  const r = eventCivilDayRange({ start_at: startIso ?? null, end_at: endIso ?? null, timezone: tz ?? null });
  if (!r || r.first === r.last) return "";
  const at = (k: string) => new Date(Date.UTC(+k.slice(0, 4), +k.slice(5, 7) - 1, +k.slice(8, 10), 12));
  return formatDateRange(at(r.first), at(r.last), { locale: _locale(), timeZone: "UTC", weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

/** A time-of-day range in ONE zone: "6:00 PM — 7:30 PM".
 *  Separator is " — " (kept stable so existing start/end splits work). */
export function friendlyTimeRange(start: DateInput, end: DateInput, tz?: string): string {
  const a = friendlyTime(start, tz);
  const b = end != null && end !== "" ? friendlyTime(end, tz) : "";
  return b ? `${a} — ${b}` : a;
}

/** Short offset label for a zone, e.g. "GMT-5". */
export function tzShortLabel(tz: string, at: DateInput = new Date()): string {
  const d = toDate(at) ?? new Date();
  try {
    const parts = new Intl.DateTimeFormat(_locale(), { timeZone: tz, timeZoneName: "shortOffset" }).formatToParts(d);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}

/** Short zone NAME on a given date, e.g. "CDT" in summer and "CST" in winter
 *  for America/Chicago — the label a wall-clock time shown in that zone carries
 *  (the public event page's form). Empty when there is no zone or an unknown
 *  one, so the caller shows no label. */
export function tzNameLabel(tz: string | null | undefined, at: DateInput = new Date()): string {
  if (!tz) return "";
  const d = toDate(at) ?? new Date();
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" }).formatToParts(d);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}

/* ── User-preferred timezone (JRNI §10.1–10.3 wire-in, 2026-08-05) ──────────
 * PRECEDENCE, declared once and for the whole app:
 *   1. the signed-in user's saved preference (Account ▸ Profile ▸ Time zone);
 *   2. the workspace/tenant display default (the runtime's datetime default,
 *      set from tenant regional settings at boot);
 *   3. the browser's zone (the runtime's fallback when nothing is configured).
 * Venue-anchored records (an event's own timezone, a branch's wall-clock
 * shift times) are NOT re-zoned by this — helpers that receive an explicit
 * `tz` keep rendering in that venue zone; this preference decides the
 * DEFAULT display zone everything else uses.
 *
 * The preference persists on the account record (updateAccountProfile →
 * profile.locale.timeZone) and is mirrored locally so it re-applies at boot
 * before the account read lands — the same pattern the locale preference
 * uses (setUserLocale + local persistence). The workspace default is
 * captured before the first override so clearing the preference restores it
 * without a reload. */
const USER_TZ_KEY = "__user_tz_pref_v1";
let _workspaceTzCapture: string | undefined | null = null; // null = not yet captured
function _validTz(tz: string | null | undefined): tz is string {
  if (!tz) return false;
  try { new Intl.DateTimeFormat("en-US", { timeZone: tz }); return true; } catch { return false; }
}
/** The saved user display-zone preference, if any. */
export function getUserTimezonePreference(): string | null {
  try { const v = localStorage.getItem(USER_TZ_KEY); return _validTz(v) ? v : null; } catch { return null; }
}
/** Apply + persist the user's display-zone preference. `null` clears it and
 *  restores the captured workspace default. */
export function applyUserTimezonePreference(tz: string | null): void {
  if (_workspaceTzCapture === null) _workspaceTzCapture = _tz(); // capture BEFORE first override
  try { if (_validTz(tz)) localStorage.setItem(USER_TZ_KEY, tz); else localStorage.removeItem(USER_TZ_KEY); } catch { /* private mode */ }
  configureDateTime({ timeZone: _validTz(tz) ? tz : _workspaceTzCapture });
}
/* ── Staff-profile default zone (2026-08-07, Pat) ────────────────────────
 * A staff member carries a default zone on their own record
 * (CanonicalStaffMember.timezone) so their appointments can be read in THEIR
 * wall clock. For the signed-in staff member that value slots into the
 * precedence declared above BETWEEN the explicit preference and the workspace
 * default:
 *   user preference  >  staff profile default  >  workspace  >  browser
 * An admin editing someone else's profile therefore never changes their own
 * display zone — only the sync hook for the SIGNED-IN member calls this.
 * Mirrored locally for the same reason the preference is: it must re-apply at
 * boot, before the roster read lands. */
const STAFF_TZ_KEY = "__staff_profile_tz_v1";
/** The mirrored default zone of the signed-in staff member, if any. */
export function getStaffProfileTimezone(): string | null {
  try { const v = localStorage.getItem(STAFF_TZ_KEY); return _validTz(v) ? v : null; } catch { return null; }
}
/** Apply + mirror the signed-in staff member's profile zone. `null` clears it. */
export function applyStaffProfileTimezone(tz: string | null): void {
  if (_workspaceTzCapture === null) _workspaceTzCapture = _tz(); // capture BEFORE first override
  try { if (_validTz(tz)) localStorage.setItem(STAFF_TZ_KEY, tz); else localStorage.removeItem(STAFF_TZ_KEY); } catch { /* private mode */ }
  if (getUserTimezonePreference()) return; // an explicit preference still wins
  const next = _validTz(tz) ? tz : _workspaceTzCapture;
  if (_tz() !== next) configureDateTime({ timeZone: next });
}
/** Which layer decided the current display zone — for honest UI labelling. */
export function displayZoneSource(): "preference" | "profile" | "workspace" {
  if (getUserTimezonePreference()) return "preference";
  if (getStaffProfileTimezone()) return "profile";
  return "workspace";
}

/** Re-assert the mirrored preference over the workspace default. Called at
 *  module load and again on window load — tenant boot config may apply after
 *  this module loads, and the user's preference must win over it. */
export function reassertUserTimezonePreference(): void {
  const pref = getUserTimezonePreference() ?? getStaffProfileTimezone();
  if (!pref) return;
  if (_workspaceTzCapture === null) _workspaceTzCapture = _tz();
  if (_tz() !== pref) configureDateTime({ timeZone: pref });
}
try {
  reassertUserTimezonePreference();
  if (typeof window !== "undefined") window.addEventListener("load", () => setTimeout(reassertUserTimezonePreference, 0));
} catch { /* never break module load */ }

/** Zone context for booking surfaces: what zone times are being shown in vs
 *  the device's zone — the honest dual-time cue JRNI §10.2/§10.3 asks for.
 *  `locationTz` (a venue's own zone) wins when given; otherwise the resolved
 *  display zone (user pref → workspace → browser). */
export function tzContext(locationTz?: string): { zone: string; label: string; browserZone: string; browserLabel: string; differs: boolean } {
  let browserZone = "UTC";
  try { browserZone = new Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { /* keep UTC */ }
  const zone = (_validTz(locationTz) ? locationTz : undefined) ?? _tz() ?? browserZone;
  return {
    zone,
    label: tzShortLabel(zone) || zone,
    browserZone,
    browserLabel: tzShortLabel(browserZone) || browserZone,
    differs: zone !== browserZone,
  };
}

/** Absolute instant (ISO, `Z`-suffixed) of a WALL CLOCK observed in `tz` —
 *  `wallClockToIso("2026-08-07", "14:00", "America/New_York")`. Accepts
 *  "14:00" and "2:00 PM". Appointment records store the branch's wall clock as
 *  separate date/start/end strings; converting one into another person's zone
 *  needs the instant first. Same two-pass IANA offset resolution as
 *  calendarDateStartIso (DST-correct, falls forward out of a spring-forward
 *  gap). Returns null when the inputs don't parse — callers then show the
 *  stored wall clock unconverted rather than a wrong time. */
export function wallClockToIso(dateKey: string, time: string, tz?: string): string | null {
  const dm = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(String(dateKey ?? "").trim());
  const tm = /^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i.exec(String(time ?? "").trim());
  if (!dm || !tm) return null;
  const y = parseInt(dm[1], 10), mo = parseInt(dm[2], 10) - 1, d = parseInt(dm[3], 10);
  let h = parseInt(tm[1], 10);
  const mi = parseInt(tm[2], 10);
  const mer = tm[3]?.toUpperCase();
  if (mer === "PM" && h < 12) h += 12;
  if (mer === "AM" && h === 12) h = 0;
  const zone = tz ?? _tz();
  const guess = Date.UTC(y, mo, d, h, mi);
  if (!zone) return new Date(y, mo, d, h, mi).toISOString();
  try {
    const offAt = (epoch: number): number => {
      const p = toParts(epoch, zone);
      return p ? Date.UTC(p.y, p.mo, p.d, p.h, p.mi) - epoch : 0;
    };
    const wallEq = (p: { y: number; mo: number; d: number; h: number; mi: number } | null) =>
      !!p && p.y === y && p.mo === mo && p.d === d && p.h === h && p.mi === mi;
    const e1 = guess - offAt(guess);
    if (wallEq(toParts(e1, zone))) return new Date(e1).toISOString();
    const e2 = guess - offAt(e1);
    if (wallEq(toParts(e2, zone))) return new Date(e2).toISOString();
    return new Date(Math.max(e1, e2)).toISOString();
  } catch {
    return null;
  }
}

/** Stable "YYYY-MM-DD" calendar-day key for `input` (default: now) AS OBSERVED
 *  IN `tz` (default: the configured display tz). Two instants share a key iff
 *  they land on the same calendar day in that zone — the tz-correct primitive
 *  for "same day?"/day-boundary decisions. */
export function calendarDateKey(input: DateInput = new Date(), tz?: string): string {
  const d = toDate(input) ?? new Date();
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: tz ?? _tz(), year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
  } catch {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
}

/** Today's calendar date (year, 0-indexed month, day) AS OBSERVED IN `tz`
 *  (default: the configured display tz; `now` injectable for tests/preview).
 *  Calendars must decide "is this cell today?" through this — NOT raw
 *  `new Date().getDate()` — so they agree with the tz-aware dashboard instead
 *  of drifting to the browser's local midnight near a day boundary
 *  (B2 / voyage#126 / REQ-029). */
export function currentCalendarDate(tz?: string, now: DateInput = new Date()): { y: number; mo: number; d: number } {
  const [y, mo, d] = calendarDateKey(now, tz).split("-").map((n) => parseInt(n, 10));
  if (!isNaN(y) && !isNaN(mo) && !isNaN(d)) return { y, mo: mo - 1, d };
  const local = toDate(now) ?? new Date();
  return { y: local.getFullYear(), mo: local.getMonth(), d: local.getDate() };
}

/** UTC instant (real ISO-8601 `.toISOString()` string, `Z`-suffixed) of the
 *  start of the calendar day of `input` (default: now) AS OBSERVED IN `tz`
 *  (default: the configured display tz) — the tenant-timezone-aware,
 *  DST-correct day boundary (voyage#126 contract; GM11/GM12 pushback).
 *  Two-pass IANA offset resolution: interpret wall-clock midnight as UTC,
 *  subtract the zone offset observed at (then near) that instant; when
 *  midnight does not exist in `tz` (a spring-forward gap crossing midnight),
 *  the corrected second pass falls forward to the first valid instant.
 *  E.g. America/New_York 2026-03-08 → "2026-03-08T05:00:00.000Z". */
export function calendarDateStartIso(input: DateInput = new Date(), tz?: string): string {
  const zone = tz ?? _tz();
  return calendarKeyStartIso(calendarDateKey(input, zone), zone);
}

/* ── Civil dates (CC 0075/0076, 29 Sept) ─────────────────────────────────────
 * A calendar column or cell IS a tenant civil date, a "YYYY-MM-DD" key, never a
 * host instant. The views keep plain Date objects for day arithmetic, built as
 * the HOST-LOCAL midnight of that civil date, so getDate()/getDay()/setDate() are
 * the civil values. Anything that crosses into instants or into the tenant zone
 * goes through these three: the key, the tenant day start of a key, and a noon
 * instant inside the tenant day for the tenant-zone formatters. (A host-local
 * midnight read in the tenant zone is the PREVIOUS day on a host ahead of it:
 * that was the defect.) */

/** "YYYY-MM-DD" of a civil Date (its host-local fields), or a key passed through. */
export function civilKey(d: Date | string): string {
  if (typeof d === "string") return d;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** An instant at 12:00 on that civil date in the tenant zone, for the
 *  friendlyCalendar* formatters (which format in the tenant zone). Noon keeps it
 *  clear of DST gaps at midnight. */
export function civilNoon(d: Date | string, tz?: string): Date {
  return new Date(Date.parse(calendarKeyStartIso(civilKey(d), tz)) + 12 * 3600 * 1000);
}

/** UTC instant of the start of the civil date `key` in `tz` (default: the display tz). */
export function calendarKeyStartIso(key: string, tz?: string): string {
  const zone = tz ?? _tz();
  const [y, mo, d] = key.split("-").map((n) => parseInt(n, 10));
  if (isNaN(y) || isNaN(mo) || isNaN(d)) return `${key}T00:00:00`;
  if (!zone) {
    // No configured zone: the runtime-local midnight, as a real instant.
    return new Date(y, mo - 1, d, 0, 0, 0, 0).toISOString();
  }
  try {
    const offAt = (epoch: number): number => {
      const p = toParts(epoch, zone);
      return p ? Date.UTC(p.y, p.mo, p.d, p.h, p.mi) - epoch : 0;
    };
    const wallEq = (p: { y: number; mo: number; d: number; h: number; mi: number } | null) =>
      !!p && p.y === y && p.mo === mo - 1 && p.d === d && p.h === 0 && p.mi === 0;
    const guess = Date.UTC(y, mo - 1, d, 0, 0);
    const e1 = guess - offAt(guess);
    if (wallEq(toParts(e1, zone))) return new Date(e1).toISOString();
    const e2 = guess - offAt(e1);
    if (wallEq(toParts(e2, zone))) return new Date(e2).toISOString();
    // Wall-clock midnight doesn't exist (spring-forward gap at midnight):
    // fall forward to the later candidate — the first valid instant of the day.
    return new Date(Math.max(e1, e2)).toISOString();
  } catch {
    return `${key}T00:00:00`;
  }
}

/** True when `input` falls on the same calendar date as `now` (default: the
 *  real current instant) in `tz`. `now` is injectable for tests/preview. */
export function isToday(input: DateInput, tz?: string, now: DateInput = new Date()): boolean {
  const d = toDate(input);
  if (!d) return false;
  return calendarDateKey(d, tz) === calendarDateKey(now, tz);
}

/** Legacy day-month-year token used by older parsers, kept for compatibility. */
export function ddmmyyToken(input: DateInput): string {
  const d = toDate(input);
  if (!d) return "";
  return `${d.getDate()} ${_MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

/** Numeric calendar parts of `input` AS OBSERVED IN `tz` (0-indexed month).
 *  Use this when you need the wall-clock y/m/d/h/min in a specific zone (e.g.
 *  positioning an event on a calendar grid), rather than the runtime zone. */
export function toParts(input: DateInput, tz?: string): { y: number; mo: number; d: number; h: number; mi: number } | null {
  const date = toDate(input);
  if (!date) return null;
  try {
    const p = new Intl.DateTimeFormat("en-US", {
      timeZone: tz ?? _tz(), year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(date);
    const get = (t: string) => parseInt(p.find((x) => x.type === t)?.value || "0", 10);
    return { y: get("year"), mo: get("month") - 1, d: get("day"), h: get("hour"), mi: get("minute") };
  } catch {
    return { y: date.getFullYear(), mo: date.getMonth(), d: date.getDate(), h: date.getHours(), mi: date.getMinutes() };
  }
}

/** #789.4 — true when the naive wall-clock (y, mo, d, h, mi) EXISTS in `tz`;
 *  false inside a DST spring-forward gap (e.g. 2026-03-08 02:30 in
 *  America/New_York — clocks jump 02:00→03:00, so 02:30 never occurs).
 *  Client-side PRE-SUBMIT warning only — the server remains authoritative
 *  (422 EVENT_LOCAL_TIME_NONEXISTENT). Unknown zones return true (never block
 *  on a helper failure). */
export function wallClockExistsInTz(parts: { y: number; mo: number; d: number; h: number; mi: number }, tz: string): boolean {
  if (!tz) return true;
  try {
    const wallEq = (p: { y: number; mo: number; d: number; h: number; mi: number } | null) =>
      !!p && p.y === parts.y && p.mo === parts.mo && p.d === parts.d && p.h === parts.h && p.mi === parts.mi;
    const offAt = (epoch: number): number => {
      const p = toParts(epoch, tz);
      return p ? Date.UTC(p.y, p.mo, p.d, p.h, p.mi) - epoch : 0;
    };
    // Standard two-pass resolution: interpret the wall clock as UTC, subtract
    // the zone offset observed at (then near) that instant; the wall clock
    // exists iff either candidate epoch formats back to the same wall clock.
    const guess = Date.UTC(parts.y, parts.mo, parts.d, parts.h, parts.mi);
    const e1 = guess - offAt(guess);
    if (wallEq(toParts(e1, tz))) return true;
    const e2 = guess - offAt(e1);
    return wallEq(toParts(e2, tz));
  } catch {
    return true;
  }
}
